import { FormCaptureInput } from 'components/common/FormCaptureInput';
import { FormDropdown } from 'components/common/FormDropdown';
import { TaskPicker } from 'components/common/TaskPicker';
import FormInput from 'components/FormInput';
import { Form } from 'react-bootstrap';
import { Checkbox, FormControlLabel } from '@mui/material';
import { AddModal } from 'components/common/AddModal';
import { ChangeEvent, useState } from 'react';
import { ObservationProps } from './types';
import { useDispatch, useSelector } from 'react-redux';
import { RootStates } from 'redux/store';
import './Observation.scss';
import moment from 'moment';
import { updateOrganizationArea } from 'redux/authSlice';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DateTimePicker } from 'components/common/DateTimePicker';

export const Observation: React.FC<ObservationProps> = ({
  observation,
  disabled,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const dispatch = useDispatch();
  const { projectId, observationId } = useParams();
  const observationAreas = useSelector(
    (state: RootStates) =>
      state.auth.userDetails.observationAreas?.map((item) => ({
        name: item,
      })) ?? [],
  );

  const severityItems = useSelector(
    (state: RootStates) =>
      state.auth.userDetails.severity?.map((item) => ({ name: item })) ?? [],
  );

  const handleActionClick = () => setShow(true);
  const handleCloseModal = () => setShow(false);
  const handleSubmitModal = (item: string) => {
    setShow(false);
    onUpdate?.({
      ...observation,
      areaOfObservations: { name: item },
    });
    dispatch(updateOrganizationArea(item));
  };

  const handleSelectNewArea = (item: { name: string }) =>
    onUpdate?.({
      ...observation,
      areaOfObservations: item,
    });

  const handleChangeDescription = (event: ChangeEvent<HTMLInputElement>) =>
    onUpdate?.({
      ...observation,
      description: event.target.value,
    });

  const handleCaptureImage = (image: string) =>
    onUpdate?.({
      ...observation,
      images: [...(observation.images ?? []), { docType: 'image', url: image }],
    });

  const handleDeleteImage = (images: string[]) =>
    onUpdate?.({
      ...observation,
      images: images.map((img) => ({ docType: 'image', url: img })),
    });

  const handleAddLocation = (location: string) =>
    onUpdate?.({
      ...observation,
      locations: [...observation.locations, location],
    });

  const handleDeleteLocation = (locations: string[]) =>
    onUpdate?.({
      ...observation,
      locations,
    });

  const getObservationTypeChangeHandler =
    (key: 'POSITIVE' | 'NEGATIVE') => () => {
      onUpdate?.({
        ...observation,
        observationType: key,
      });
    };

  const handleCloseDateNoteRequiredCheck = (event: React.ChangeEvent<{}>) =>
    onUpdate?.({
      ...observation,
      isCloseTillRequired: !(event.target as HTMLInputElement).checked,
    });

  const handleDateChange = (value: Date) => {
    onUpdate?.({
      ...observation,
      closeTill: value.toISOString(),
    });
  };

  const handleSelectSeverity = (value: { name: string }) => {
    onUpdate?.({
      ...observation,
      severity: value.name,
    });
  };

  return (
    <div className="d-flex flex-column observation-form">
      <div className="d-flex flex-column flex-md-row">
        <FormDropdown<{ name: string }>
          lable={t('label_area_of_observation')}
          items={observationAreas}
          required
          disabled={disabled}
          selectedItem={observation.areaOfObservations.name}
          action={t('btn_add_new_area')}
          onSelect={handleSelectNewArea}
          onActionClick={handleActionClick}
        />
        <span className="empty-placeholder" />
      </div>

      <div className="d-flex flex-column flex-md-row">
        <FormInput
          label={t('label_description')}
          type="text"
          value={observation.description}
          required
          disabled={disabled}
          name="description"
          as="textarea"
          onChange={handleChangeDescription}
        />
        <span className="empty-placeholder" />
      </div>
      <div className="d-flex flex-column flex-md-row">
        <FormDropdown<{ name: string }>
          required
          items={severityItems}
          selectedItem={observation.severity}
          disabled={disabled}
          lable={t('label_severity')}
          onSelect={handleSelectSeverity}
        />
        <FormCaptureInput
          label={t('label_add_photo')}
          images={observation.images.map((img) => img.url)}
          limit={3}
          disabled={disabled}
          required={false}
          path={`projects/${projectId}/observations/${observationId}/${
            (observation.images?.length ?? 0) + 1
          }`}
          onCapture={handleCaptureImage}
          onDelete={handleDeleteImage}
        />
      </div>
      <div className="d-flex flex-column flex-md-row">
        <TaskPicker
          items={observation.locations}
          allowSpace
          required
          additionalInfo={t('hashtag_additional_info_observation')}
          disabled={disabled}
          onAddNewItem={handleAddLocation}
          onRemoveItem={handleDeleteLocation}
        />
        <div className="d-flex flex-column observation-type">
          <Form.Label>
            {t('label_observation_type')}
            <span>*</span>
          </Form.Label>
          <div className="d-flex">
            <FormControlLabel
              className="checkbox-not-required"
              checked={observation.observationType === 'POSITIVE'}
              control={<Checkbox color="primary" />}
              disabled={disabled}
              label={t('label_positive')}
              onClick={getObservationTypeChangeHandler('POSITIVE')}
            />
            <FormControlLabel
              className="checkbox-not-required"
              checked={observation.observationType === 'NEGATIVE'}
              disabled={disabled}
              control={<Checkbox color="primary" />}
              label={t('label_negative')}
              onClick={getObservationTypeChangeHandler('NEGATIVE')}
            />
          </div>
        </div>
      </div>
      <div className="d-flex flex-column close-info">
        <Form.Label>
          {t('label_close_till')}
          <span>*</span>
        </Form.Label>
        <div
          className="d-flex align-items-start align-items-md-center
 flex-column flex-md-row"
        >
          <FormControlLabel
            className="checkbox-not-required"
            disabled={disabled}
            checked={!observation.isCloseTillRequired}
            control={<Checkbox color="primary" />}
            label={t('label_not_required')}
            onChange={handleCloseDateNoteRequiredCheck}
          />
          <span className="d-block mr-1 or-placeholder">OR</span>
          <DateTimePicker
            value={moment(observation.closeTill!).toDate()}
            label={t('label_pick_date')}
            disabled={!observation.isCloseTillRequired || disabled}
            onChange={handleDateChange}
          />
        </div>
      </div>
      <AddModal
        open={show}
        title={t('modal_title_observation_area')}
        inputLabel={t('label_add_new_area')}
        onSubmit={handleSubmitModal}
        onClose={handleCloseModal}
      />
    </div>
  );
};
